import EventManager from '../eventManager/EventManager'
import type PrebidFacade from '../prebidFacade/PrebidFacade'
import type { Slot } from '../slots/Slot'
import Refreshable from './Refreshable'

/**
 * The refresh class is responsible for reselling and displaying a
 * slot after a specific amount of time. It listens for slots that
 * pass through the impression viewable event. If the slot has a
 * valid refresh time property, it instantiates a refreshable class
 * for it. The refreshable class sets a 30 second timeout to trigger
 * a refresh. After 30 seconds, if the slot is still viewable, it
 * is refreshed and a refreshable is reinstantiated for it until the
 * max number of refreshes is reached. In the event that a slot loses
 * visibility, the refresh is paused until the slot becomes visible
 * again.
 */

export default class Refresh {
  registry: { [id: string]: Refreshable | null } = {}

  constructor(public prebidFacade: PrebidFacade) {
    EventManager.on(EventManager.events.slotRenderEnded, (e, slot) =>
      this.registerNewRefreshable(slot)
    )

    EventManager.on(EventManager.events.registerRefreshable, (slot) => {
      this.registerNewRefreshable(slot)
    })

    EventManager.on(EventManager.events.shortCircuitRefresh, (slot) => {
      const refreshable = this.registry[slot.id]
      if (refreshable) {
        refreshable.shortCircuit()
      }
    })

    EventManager.on(EventManager.events.resetRefreshable, (slot) => {
      const refreshable = this.registry[slot.id]
      if (refreshable) {
        refreshable.reset()
      }
    })
  }

  registerNewRefreshable(slot: Slot | undefined): void {
    if (!slot || !isRefreshingSlot(slot)) {
      return
    }

    this.registry[slot.id] =
      this.registry[slot.id] || new Refreshable(slot, this)
  }
}

export function isRefreshingSlot(slot: Pick<Slot, 'refreshTime'>): boolean {
  return !!slot.refreshTime && slot.refreshTime >= 0
}
